import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import {
  findMidiOutput,
  requestMidiAccess,
  type MidiAccessState,
} from "@midi-fantom-utils/player-core";
import type { MidiPortSelection } from "../config.js";

type MidiOutputSettingsProps = {
  value: MidiPortSelection | null;
  onChange: (port: MidiPortSelection) => void;
};

/**
 * Настройки MIDI-выхода: запрос доступа к Web MIDI API (по клику — user
 * gesture), список output-портов и выбор устройства (по умолчанию — то,
 * где в имени есть «fantom»; сохранённый порт восстанавливается по id/name).
 *
 * `requestMidiAccess` обязателен по user-gesture (браузер покажет permission
 * prompt), поэтому первый клик «Подключить» — это и есть gesture.
 */
export const MidiOutputSettings = ({
  value,
  onChange,
}: MidiOutputSettingsProps) => {
  const [access, setAccess] = useState<MidiAccessState>({ kind: "idle" });
  const [selectedId, setSelectedId] = useState<string>(value?.id ?? "");

  // Сохранённый выбор (из настроек) становится значением селекта.
  useEffect(() => {
    if (value) setSelectedId(value.id);
  }, [value]);

  const connect = useCallback(async () => {
    const next = await requestMidiAccess();
    setAccess(next);
    if (next.kind === "granted") {
      // Восстанавливаем сохранённый порт; иначе — дефолт (/fantom/i).
      const device = findMidiOutput(next.outputs, {
        id: value?.id,
        name: value?.name,
      });
      if (device) {
        setSelectedId(device.id);
        if (device.id !== value?.id) {
          onChange({ id: device.id, name: device.name });
        }
      }
    }
  }, [value, onChange]);

  const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedId(id);
    if (access.kind !== "granted") return;
    const device = access.outputs.find((d) => d.id === id);
    if (device) onChange({ id: device.id, name: device.name });
  };

  return (
    <div className="settings-section">
      <p className="muted">
        MIDI-выход: ноты отправляются в подключённое устройство — Fantom 06
        (USB-MIDI) или GM-синтезатор ОС (Microsoft General MIDI, fluidsynth/
        TiMidity на Linux) — как в обычный MIDI-порт.
      </p>

      {access.kind === "idle" || access.kind === "denied" ? (
        <button type="button" className="btn" onClick={connect}>
          🔌 Подключить MIDI-устройство
        </button>
      ) : null}

      {access.kind === "granted" ? (
        <label className="settings-row">
          <span className="settings-row__label">Устройство</span>
          <select
            value={selectedId}
            onChange={onSelect}
            disabled={access.outputs.length === 0}
            aria-label="MIDI-устройство вывода"
          >
            {access.outputs.length === 0 ? (
              <option value="">Устройства не найдены</option>
            ) : (
              access.outputs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.manufacturer ? ` (${d.manufacturer})` : ""}
                </option>
              ))
            )}
          </select>
          <button type="button" className="btn btn--ghost" onClick={connect}>
            Обновить
          </button>
        </label>
      ) : null}

      {access.kind === "unsupported" ? (
        <p className="muted">
          Web MIDI не поддерживается этим браузером. Используйте Chrome/Edge
          (на Linux — не-Flatpak) или Firefox 108+.
        </p>
      ) : null}
      {access.kind === "not-secure" ? (
        <p className="muted">
          Web MIDI работает только по HTTPS (или на localhost).
        </p>
      ) : null}
      {access.kind === "denied" ? (
        <p className="muted">
          Доступ к MIDI запрещён — разрешите его в настройках браузера, затем
          нажмите «Подключить» ещё раз.
        </p>
      ) : null}

      <p className="muted" style={{ fontSize: 12 }}>
        Linux: чтобы появился системный GM-синтезатор, запустите, например,
        <code> fluidsynth -a alsa GeneralUserGS_v1.471.sf2</code> или{" "}
        <code>timidity -iA</code>.
      </p>
    </div>
  );
};